import { S3Client, GetObjectCommand, PutObjectCommand, HeadObjectCommand } from '@aws-sdk/client-s3';
import { AppState } from '../types';

class S3StateService {
  private s3Client: S3Client;
  private bucketName: string;
  private stateKey: string;

  constructor() {
    this.bucketName = import.meta.env.VITE_S3_BUCKET_NAME || '';
    this.stateKey = import.meta.env.VITE_S3_STATE_KEY || 'state/app-state.json';
    
    this.s3Client = new S3Client({
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    });
  }

  /**
   * Fetch state from S3 with retry logic
   * Handles 404 errors by initializing default state
   */
  async getState(): Promise<AppState> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const command = new GetObjectCommand({
          Bucket: this.bucketName,
          Key: this.stateKey,
        });

        const response = await this.s3Client.send(command);
        
        if (!response.Body) {
          throw new Error('Empty response body from S3');
        }

        const bodyString = await response.Body.transformToString();
        const state = JSON.parse(bodyString) as AppState;
        
        return state;
      } catch (error: any) {
        // Handle 404 - file doesn't exist yet, initialize with default state
        if (error.name === 'NoSuchKey' || error.$metadata?.httpStatusCode === 404) {
          const defaultState = this.getDefaultState();
          // Try to create the file with default state
          try {
            await this.putState(defaultState);
          } catch (putError) {
            console.warn('Failed to initialize default state in S3:', putError);
          }
          return defaultState;
        }

        lastError = error;
        
        // Don't retry on certain errors
        if (error.name === 'AccessDenied' || error.$metadata?.httpStatusCode === 403) {
          throw new Error('S3 access denied. Check bucket permissions and CORS configuration.');
        }

        // Exponential backoff for retries
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          await this.sleep(delay);
        }
      }
    }

    throw new Error(`Failed to fetch state from S3 after ${maxRetries} attempts: ${lastError?.message}`);
  }

  /**
   * Merge and save state to S3 with retry logic
   */
  async updateState(updates: Partial<AppState>): Promise<void> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Get current state
        let currentState: AppState;
        try {
          currentState = await this.getState();
        } catch (error) {
          // If we can't get current state, start with default
          currentState = this.getDefaultState();
        }

        // Merge updates
        const newState: AppState = {
          ...currentState,
          ...updates,
          challenges: {
            ...currentState.challenges,
            ...(updates.challenges || {}),
          },
          powerBreak: {
            ...currentState.powerBreak,
            ...(updates.powerBreak || {}),
          },
          lastUpdated: new Date().toISOString(),
        };

        // Save to S3
        await this.putState(newState);
        return;
      } catch (error: any) {
        lastError = error;

        // Don't retry on certain errors
        if (error.name === 'AccessDenied' || error.$metadata?.httpStatusCode === 403) {
          throw new Error('S3 access denied. Check bucket permissions and CORS configuration.');
        }

        // Exponential backoff for retries
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          await this.sleep(delay);
        }
      }
    }

    throw new Error(`Failed to update state in S3 after ${maxRetries} attempts: ${lastError?.message}`);
  }

  /**
   * Check if S3 is accessible
   */
  async isAvailable(): Promise<boolean> {
    try {
      const command = new HeadObjectCommand({
        Bucket: this.bucketName,
        Key: this.stateKey,
      });

      await this.s3Client.send(command);
      return true;
    } catch (error: any) {
      // 404 is acceptable - file doesn't exist yet but bucket is accessible
      if (error.name === 'NotFound' || error.$metadata?.httpStatusCode === 404) {
        return true;
      }
      
      console.warn('S3 is not available:', error.message);
      return false;
    }
  }

  /**
   * Helper method to put state to S3
   */
  private async putState(state: AppState): Promise<void> {
    const command = new PutObjectCommand({
      Bucket: this.bucketName,
      Key: this.stateKey,
      Body: JSON.stringify(state, null, 2),
      ContentType: 'application/json',
    });

    await this.s3Client.send(command);
  }

  /**
   * Get default state when S3 file doesn't exist
   */
  private getDefaultState(): AppState {
    return {
      challenges: {},
      powerBreak: {
        isActive: false,
        startedAt: null,
        duration: 180,
      },
      teamAssignments: {},
      randomizationHistory: {
        assignments: [],
        participantPairings: {},
      },
      lastUpdated: new Date().toISOString(),
    };
  }

  /**
   * Sleep helper for exponential backoff
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const s3Service = new S3StateService();
